import { createSlice } from '@reduxjs/toolkit';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';

/**
 * The last failed request, written by the app's RTK Query error middleware and
 * shown by the page layouts as a global error notification.
 */
export interface RtkQueryErrorState {
  error: null | {
    title: string;
    description?: string;
  };
  status: string | null;
  message: string | null;
}

export const initialState: RtkQueryErrorState = {
  error: null,
  status: null,
  message: null,
};

const DEFAULT_ERROR_TITLE = 'Network error';
const DEFAULT_ERROR_DESCRIPTION =
  'The server is not responding or a network error occurred. Please try again later.';

/**
 * Turns a rate-limited response's `retryAfterSeconds` (e.g. a 429 from a
 * Telegram-backed endpoint such as the trading-group invite link) into the same
 * "Please try again in ~Xs." wording the trading-group job notifications use for
 * a flood-wait. The payload is untyped server JSON, so anything but a positive,
 * finite number is ignored; a fractional wait rounds up so retrying at the
 * hinted time is never too early.
 */
const formatRetryHint = (retryAfterSeconds: unknown): string | undefined =>
  typeof retryAfterSeconds === 'number' &&
  Number.isFinite(retryAfterSeconds) &&
  retryAfterSeconds > 0
    ? `Please try again in ~${Math.ceil(retryAfterSeconds)}s.`
    : undefined;

const rtkQueryErrorSlice = createSlice({
  name: 'rtkQueryError',
  initialState,
  reducers: {
    setError(
      state,
      action: PayloadAction<{
        data?: {
          message?: string;
          error?: string;
          errors?: { message: string }[];
          retryAfterSeconds?: unknown;
        };
        status: number | string | null;
      }>,
    ) {
      // If there is an errors array, use message as title and errors[i].message as description
      const { data, status } = action.payload;
      let title = data?.message ?? data?.error ?? DEFAULT_ERROR_TITLE;
      const retryHint = formatRetryHint(data?.retryAfterSeconds);
      let description: string | undefined = retryHint;

      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        // Use the main message as title, and join all errors as description
        title = `Error: ${data.message ?? DEFAULT_ERROR_TITLE}`;
        const errorsDescription = data.errors.map((e) => e.message).join('; ');
        description = retryHint ? `${errorsDescription} ${retryHint}` : errorsDescription;
      } else if (!data?.message && !data?.error) {
        // A known wait is more actionable than the generic network-error copy.
        description = retryHint ?? DEFAULT_ERROR_DESCRIPTION;
      }

      state.error = title ? { title, description } : null;
      state.status = status === null ? null : String(status);
    },
    clearError(state) {
      state.error = null;
      state.status = null;
      state.message = null;
    },
  },
});

/**
 * `setError` maps a rejected request's payload to a notification title/description;
 * `clearError` resets the slice once the notification has been shown.
 */
export const rtkQueryErrorActions = rtkQueryErrorSlice.actions;
export default rtkQueryErrorSlice.reducer;

/** Any action created by {@link rtkQueryErrorActions}. */
export type RtkQueryErrorAction = ReturnType<
  (typeof rtkQueryErrorActions)[keyof typeof rtkQueryErrorActions]
>;

/**
 * A dispatch narrowed to this slice's actions, for consumers that only write
 * the request error (`useDispatch<RtkQueryErrorDispatch>()`). The app's root
 * dispatch satisfies it (checked in `store/package-store-contracts.test.ts`).
 */
export type RtkQueryErrorDispatch = Dispatch<RtkQueryErrorAction>;
